// Cases API
import type { HypothesisConfidence, HypothesisVerdict } from '@/types/analytic-hypothesis'
import { apiClient } from './client'

export interface Case {
  id: string
  name: string
  description?: string | null
  status: string
  created_at: string
  updated_at: string
  document_count?: number
  entity_count?: number
  workflow_count?: number
}

export interface InitialEntity {
  name: string
  entity_type: 'Person' | 'Company'
  is_suspect: boolean
}

export interface CreateCaseRequest {
  name: string
  description?: string
  initial_entities?: InitialEntity[]
}

export interface UpdateCaseRequest {
  status?: string
  description?: string
}

export interface Document {
  id: string
  filename: string
  file_size: number
  file_type: string
  status: 'uploaded' | 'processing' | 'processed' | 'failed' | 'analyzing' | 'deleted'
  anchor_entity_id?: string | null
  anchor_entity_name?: string | null
  anchor_entity_type?: string | null
  created_at: string
  processed_at?: string | null
  // Async analysis tracking
  analysis_status?: 'pending' | 'analyzing' | 'completed' | 'failed' | null
  analysis_result?: DocumentAnalysisResult | null
  // Lake import linkage — set when this doc was cloned from a lake source
  child_of?: string | null
}

/** A data-lake document imported into a case via an extraction scope. */
export interface LakeImport {
  /** Child document ID */
  id: string
  /** Parent lake document ID */
  child_of: string
  filename: string
  file_size: number
  file_type: string
  status: string
  created_at: string
  /** Parquet metadata inherited from parent at scope creation */
  has_parquet: boolean | null
  parquet_path: string | null
  /** DataLabel.name of the parent lake document, e.g. "green" */
  parent_label_name: string | null
  /** DataLabel hex color, e.g. "#22c55e" */
  parent_label_color: string | null
  /** Extraction scope summary */
  scope_id: string | null
  scope_type: 'all' | 'chunks' | 'tabular' | null
  /** Number of selected chunks; null when scope_type is "all" */
  chunk_count: number | null
  scope_note: string | null
  /** True if extraction schema has been reviewed by user */
  schema_reviewed?: boolean
  /** Phase-specific status fields for accurate extraction pipeline tracking */
  phase0_status?: string // pending | running | completed | failed
  phase1_status?: string // pending | running | completed | failed
  phase2_status?: string // pending | running | completed | failed
  /** Error message when extraction fails */
  analysis_error?: string
  /** True when geo_parquet_path is set (geospatial data was extracted) */
  has_geo_data?: boolean
}

/**
 * Where the latest job of an audio document is: it waits in the queue, the speech is
 * transcribed, the transcript is indexed for search, the entities are extracted, and it is done.
 */
export type ProcessingStageDto = 'queued' | 'transcript' | 'indexing' | 'entities' | 'done'

export type TabularExtractionMode = 'structured' | 'llm'

export interface TableProcessingResponse {
  document_id: string
  tabular_extraction_mode: TabularExtractionMode
}

export interface CaseDocumentResponse {
  id: string
  filename: string
  file_size: number
  file_type: string | null
  status: string
  child_of?: string | null
  copied_at?: string | null
  visibility?: string | null
  created_at: string
  /** When the case received the source (ADR-0094): the upload time unless a date was declared. */
  received_at?: string
  processed_at?: string | null
  /** A HUMINT capture: immutable evidence, never replaced, re-extracted or deleted (ADR-0045). */
  is_capture?: boolean
  /** The stage of an audio document's latest job; null for any other document, absent on an older server. */
  stage?: ProcessingStageDto | null
  /** The stage that job failed in, else null. */
  failed_stage?: ProcessingStageDto | null
  tabular_extraction_mode?: TabularExtractionMode
}

/** A row of `POST /v1/cases/{id}/documents/progress`: where a document a page follows stands. */
export interface CaseDocumentProgress {
  id: string
  status: string
  /** As on {@link CaseDocumentResponse}: null for every document that is no transcribed audio file. */
  stage: ProcessingStageDto | null
  failed_stage: ProcessingStageDto | null
}

/** What `POST /v1/cases/{id}/documents/upload` answers. */
export interface CaseDocumentUploadResponse {
  document_id: string
  filename: string
  file_size: number
  file_type: string | null
  status: string
  created_at: string
  received_at?: string
  tabular_extraction_mode?: TabularExtractionMode
}

/** What `PATCH /v1/cases/{id}/documents/{documentId}` answers: the document as the change left it. */
export interface CaseDocumentUpdateResponse {
  id: string
  received_at: string
}

export interface DocumentEntity {
  id: string
  label: string
  name: string | null
  properties: Record<string, unknown>
}

export interface DocumentRelationship {
  id: string
  type: string
  source_name: string | null
  target_name: string | null
  properties: Record<string, unknown>
}

export interface RoutingFacts {
  extension?: string | null
  pdf_text_density?: number | null
  file_type?: string | null
  document_type?: string | null
  summary?: string | null
  key_findings?: string[] | null
  content_extraction_preview?: Record<string, unknown> | null
  analysis_confidence?: number | null
  analysis_status?: 'skipped' | 'completed' | 'failed' | null
  is_scanned?: boolean | null
  [key: string]: unknown
}

export interface CaseDocumentImportInfo {
  /** Server-side `case_document_imports.id`. */
  id: string
  lake_document_id: string
  scope_type: 'all' | 'pages' | 'tabular'
  /** 1-based page numbers; null unless scope_type='pages'. */
  selected_pages: number[] | null
  /** Column names kept; null unless scope_type='tabular'. */
  selected_columns: string[] | null
  /** 0-based `__row_index` values kept; null unless scope_type='tabular'. */
  selected_rows: number[] | null
  scope_note: string | null
  created_at: string
  updated_at: string
}

export interface CaseDocumentDetail {
  id: string
  filename: string
  file_size: number
  file_type: string | null
  status: string
  tabular_extraction_mode?: TabularExtractionMode
  created_at: string
  /** When the case received the source (ADR-0094); absent on an older server. */
  received_at?: string
  /** The reader's role in the case (a superuser reads as owner): a collaborator may change the received date. */
  member_role?: CaseRole
  processed_at: string | null
  child_of: string | null
  visibility: string | null
  anchor_entity_name: string | null
  anchor_entity_type: string | null
  entity_count: number
  entities: DocumentEntity[]
  /** Exact per-label totals; `entities` is a bounded per-label sample. */
  entity_label_counts?: Record<string, number>
  relationship_count?: number
  relationships: DocumentRelationship[]
  relationship_type_counts?: Record<string, number>
  geo_capability: GeoCapability | null
  schema_name: string | null
  schema_version: string | null
  pipeline: string | null
  user_prompt: string | null
  routing_facts: RoutingFacts | null
  /** Lake-import scope. Null for directly uploaded case documents. */
  import_scope: CaseDocumentImportInfo | null
  /** Error from the latest failed extraction job. Null unless status='failed'. */
  failure_reason: string | null
  /** Faces of this document confirmed to depict a person; drives the re-extraction guard. */
  confirmed_face_count?: number
  /** Factual VLM description of an image; null for PDFs and other non-images. */
  description?: string | null
  /** A HUMINT capture: immutable evidence, never reprocessed (ADR-0045). */
  is_capture?: boolean
  /** The stage of an audio document's latest job; null for any other document. */
  stage?: ProcessingStageDto | null
  /** The stage that job failed in, else null. */
  failed_stage?: ProcessingStageDto | null
}

export interface CaseGraphEntityScoreComponents {
  evidence_diversity: number | null
  source_diversity: number | null
  target_proximity: number | null
  connections: number | null
}

export interface CaseGraphEntityDetail {
  id: string
  labels: string[]
  name: string | null
  properties: Record<string, unknown>
  investigation_score: number | null
  score_components: CaseGraphEntityScoreComponents | null
  connections: number
  sources: number
  is_suspect: boolean
  match_confidence: number | null
}

export interface CaseGraphRelationshipEndpointRef {
  id: string
  name: string | null
}

export interface CaseGraphRelationshipDetail {
  id: string
  type: string
  source: CaseGraphRelationshipEndpointRef
  target: CaseGraphRelationshipEndpointRef
  properties: Record<string, unknown>
}

/** The answer of "Analyse video": the queued job of the video lane. */
export interface VideoAnalysisStart {
  job_id: string
  run_id: string
  document_id: string
  status: 'scheduled'
}

export interface CaseDocumentInternal {
  nodes: DocumentEntity[]
  relationships: DocumentRelationship[]
}

export type CaseDocumentStatusFilter =
  | 'uploaded'
  | 'analyzing'
  | 'processing'
  | 'processed'
  | 'failed'

export type CaseFileCountByStatus = Record<CaseDocumentStatusFilter, number>

export type CaseStatusCounts = Record<'all' | CaseDocumentStatusFilter, number>

export interface CaseFolderNode {
  name: string
  path: string
  full_path: string
  file_count: number
  direct_file_count: number
  has_children: boolean
  file_count_by_status?: CaseFileCountByStatus
  /** HUMINT captures anywhere below: the folder cannot be deleted or re-extracted (ADR-0045). */
  capture_count?: number
}

export interface CaseFileNode {
  id: string
  name: string
  path: string
  document: CaseDocumentResponse
}

/** The order of the documents list's files: by upload or by processing time, newest or oldest first. */
export interface CaseDocumentSort {
  by: 'uploaded' | 'processed'
  order: 'asc' | 'desc'
}

export interface CaseFolderTreeResponse {
  folders: CaseFolderNode[]
  files: CaseFileNode[]
  total_files?: number
  has_more?: boolean
  status_counts?: CaseStatusCounts
}

export interface PipelineActivity {
  id: string
  workflow_id?: string | null
  document_id?: string | null
  document_filename?: string | null
  status: string
  created_at: string
  processed_at?: string | null
  error?: string | null
}

export interface PipelineActivityTemplate {
  id: string
  name: string
  short_name: string
  description: string
  category: 'extract' | 'transform' | 'load'
  icon: string
  color: string
}

export interface ExtractRequest {
  document_ids: string[]
  folder_paths?: string[]
  pipeline: string
  schema_name?: string
  schema_version?: number
  user_prompt?: string
}

export interface ExtractionSchema {
  id: string
  name: string
  description?: string | null
  version: number
  is_active: boolean
}

export interface ExtractResponse {
  extraction_run_id: string
  total: number
  started: number
  failed: number
  errors: Array<{ document_id: string; error: string }>
  /**
   * Documents left alone because they are being extracted already — an audio file is processed
   * from the moment it is uploaded. Absent on an older server.
   */
  skipped?: number
  /** Documents left alone with a reason: a video the deployment cannot analyse (PRD-0074). Absent before. */
  held?: Array<{ document_id: string; code: string }>
}

export type ExtractionRunStatus = 'running' | 'completed' | 'completed_with_errors' | 'failed'

export interface ExtractionRun {
  id: string
  schema_name: string | null
  schema_version: string | null
  instructions: string | null
  status: ExtractionRunStatus
  created_at: string
  started_at: string | null
  completed_at: string | null
  total_documents: number
  completed_documents: number
  failed_documents: number
  running_documents: number
  pending_documents: number
  progress_percent: number
  elapsed_ms: number | null
  eta_ms: number | null
  eta_reason: string | null
  active_document_name: string | null
  active_step_name: string | null
}

export interface ExtractionStepItem {
  name: string
  status: string
  duration_ms?: number | null
  error?: string | null
}

export interface ExtractionRunWorkflow {
  document_id?: string | null
  filename?: string | null
  file_type?: string | null
  pipeline?: string | null
  status: string
  created_at: string
  started_at?: string | null
  processed_at?: string | null
  // v2-only: when present, render the per-step drilldown.
  job_id?: string | null
  steps?: ExtractionStepItem[] | null
  eta_ms?: number | null
  eta_reason?: string | null
  // Dispatch-worker routing facts (analyze_document probe + resolver
  // output). Surfaced in the admin extraction-run detail view.
  routing_facts?: RoutingFacts | null
}

export interface ExtractionRunDetail extends ExtractionRun {
  workflows: ExtractionRunWorkflow[]
}

export interface WizardGraphNode {
  id: string
  type: string
  label: string
  entity_type: string
  color: string
  is_anchor: boolean
  x?: number
  y?: number
}

export interface WizardGraphEdge {
  id: string
  source: string
  target: string
  label: string
  relationship_type: string
  color: string
}

export type ConditionOperator =
  | 'non_empty'
  | 'empty'
  | 'equals'
  | 'not_equals'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'

export type ConditionLogic = 'and' | 'or'

export interface RowConditionClause {
  column: string
  operator: ConditionOperator
  value?: string | null
}

export interface RowCondition {
  logic: ConditionLogic
  clauses: RowConditionClause[]
}

export interface WizardEntityConfig {
  entity_type: string
  label: string
  columns: string[]
  identifier_column?: string | null
  properties_mapping: Record<string, string>
  static_properties: Record<string, unknown>
  color: string
  row_condition?: RowCondition | null
}

export interface WizardRelationshipConfig {
  relationship_type: string
  source_entity: string
  target_entity: string
  source_entity_label?: string
  target_entity_label?: string
  label: string
  properties_mapping: Record<string, string>
  static_properties: Record<string, unknown>
  color: string
}

export interface AnchorNodeConfig {
  entity_type: string
  properties: Record<string, unknown>
  role: string
  edge_type: string
  edge_properties_columns: Record<string, string>
}

export interface GeoMappingConfig {
  latitude_column: string
  longitude_column: string
  timestamp_column: string
  altitude_column?: string | null
  timestamp_format?: string | null
  metadata_columns: Record<string, string>
  entity_mappings: Array<{
    entity_type: string
    entity_identifier_column: string
    entity_name_column: string
    entity_property_columns: Record<string, string>
  }>
  derived_entities?: unknown[]
  derived_relationships?: unknown[]
}

export interface WizardConfiguration {
  name: string
  description: string
  data_pattern: string
  row_model: string
  suggested_pipeline: string
  processing_mode?: string
  entities: WizardEntityConfig[]
  relationships: WizardRelationshipConfig[]
  timestamp_column?: string | null
  unmapped_columns: string[]
  graph_nodes: WizardGraphNode[]
  graph_edges: WizardGraphEdge[]
  anchor_node?: AnchorNodeConfig | null
  confidence: number
  explanation: string
  geo_mapping?: GeoMappingConfig | null
}

export interface TXTLogColumnMapping {
  column_index: number
  column_name: string
  map_to: string
  sample_values: string[]
  detected_type: string
}

export interface TXTLogWizardConfig {
  target_entity: string
  target_entity_properties: string[]
  detected_delimiter: string
  has_header: boolean
  total_lines: number
  column_mappings: TXTLogColumnMapping[]
  sample_rows: string[][]
  structure_explanation: string
  confidence: number
  pipeline_name: string
  pipeline_description: string
}

export interface ExcelSection {
  name: string
  section_type: string
  start_row: number
  end_row: number
  headers: string[]
  description: string
  row_count: number
  data_preview: string[][]
}

export interface ExcelAnalysis {
  sheet_name: string
  total_rows: number
  total_columns: number
  last_filled_row: number
  sections: ExcelSection[]
  has_multiple_sheets: boolean
  additional_sheets: string[]
}

export interface CSVColumnInfo {
  name: string
  detected_type: string
  sample_values?: string[]
}

export interface CSVAnalysis {
  columns: CSVColumnInfo[]
  row_count: number
  detected_delimiter?: string | null
  possible_entities?: string[]
  possible_relationships?: string[]
  data_pattern?: string | null
  wizard_config?: WizardConfiguration | null
}

export interface DocumentAnalysisResult {
  filename: string
  file_size: number
  file_extension: string
  mime_type?: string | null
  file_type: string
  document_type: string
  confidence: number
  suggested_pipeline: string
  pipeline_display_name: string
  summary: string
  key_findings: string[]
  pdf_analysis?: unknown | null
  csv_analysis?: CSVAnalysis | null
  excel_analysis?: ExcelAnalysis | null
  txt_analysis?: unknown | null
  content_extraction_preview?: unknown | null
  // Top-level wizard config promoted from csv_analysis.wizard_config
  wizard_config?: WizardConfiguration | null
  // Top-level TXT log wizard config promoted from txt_analysis.txt_log_wizard_config
  txt_log_wizard_config?: TXTLogWizardConfig | null
  estimated_processing_time: string
  warnings: string[]
}

export interface StartAnalysisResponse {
  document_id: string
  analysis_status: string
  message: string
}

export interface AnalysisStatusResponse {
  document_id: string
  analysis_status: 'pending' | 'analyzing' | 'completed' | 'failed'
  analysis_result?: DocumentAnalysisResult | null
  error?: string | null
}

export interface GraphNode {
  id: string
  labels: string[]
  properties: Record<string, any>
  source_documents?: Array<{
    id: string
    name: string
    path: string
  }>
}

export interface GraphEdge {
  id: string
  type: string
  startNodeId: string
  endNodeId: string
  properties?: Record<string, any>
}

export interface GraphResponse {
  nodes: GraphNode[]
  relationships: GraphEdge[]
  total_nodes?: number
  total_edges?: number
  counts_by_type?: Record<string, number>
  limit_per_type?: number
  case_id: string
  database: string
}

export interface GeoCapability {
  tier: 'animated' | 'static' | 'none'
  animated_entity_count: number
  static_location_count: number
  time_range_start: string | null
  time_range_end: string | null
}

export interface CaseSettingsResponse {
  schema_id: string | null
  schema_name: string | null
  show_internal_nodes: boolean
  /** When true, the Case Roadmap timeline renders on the Overview page. */
  show_case_roadmap: boolean
  /** Off hides "Structured ingest" from the case menu; the view stays reachable from a document. */
  show_structured_ingest: boolean
  additional_agent_context: string | null
  /** Max tool calls (find_entity, run_cypher, …) per hypothesis run. */
  hypothesis_run_step_cap: number
  /** Wall-clock cap in seconds — bounds total run duration. */
  hypothesis_run_wall_clock_seconds: number
  /** Max tokens per LLM response. */
  hypothesis_run_max_tokens: number
  /** Max number of `create_finding` calls (cards) per run. */
  hypothesis_run_max_findings: number
  /** Per-case extension toggles. Sparse: missing key for a globally-enabled
   *  extension means "on". Keys for globally-disabled extensions are
   *  filtered out by the backend at read time. */
  extensions: Record<string, boolean>
}

export interface CaseSettingsUpdate {
  schema_id?: string | null
  show_internal_nodes?: boolean
  show_case_roadmap?: boolean
  show_structured_ingest?: boolean
  additional_agent_context?: string | null
  hypothesis_run_step_cap?: number
  hypothesis_run_wall_clock_seconds?: number
  hypothesis_run_max_tokens?: number
  hypothesis_run_max_findings?: number
  extensions?: Record<string, boolean>
}

// ─── Case Summary ─────────────────────────────────────────────────────────
// Pre-flight status the overview card uses to pick the right empty-state
// CTA: no investigations → "start research"; investigations marked but no
// summary yet → "Generate Case Summary"; summary exists → render content.
export interface CaseSummaryStatus {
  has_summary: boolean
  generated_at: string | null
  eligible_investigation_count: number
  /** The case's hypotheses (PRD-0075 R-E5): a summary builds from them as well. */
  hypothesis_count?: number
}

// Shape of the cached payload. ``payload`` mirrors what the PDF template's
// ``fetch_data`` returns so the FE renderer stays aligned with the PDF.
// Kept as ``unknown`` here — individual subfields (case, investigations, …)
// are typed inside the consumer components where they're actually destructured.
export interface CaseSummaryEnvelope {
  payload: CaseSummaryPayload
  generated_at: string
}

export interface CaseSummaryPayload {
  case: {
    id: string
    name: string
    description: string | null
    status: string | null
    documentCount: number
    entityCount: number
    relationshipCount: number
    createdAt: string
    updatedAt: string
  }
  ai_summary: string
  investigations: CaseSummaryInvestigation[]
  /** The case's key judgments; absent from a summary generated before hypotheses existed. */
  hypotheses?: CaseSummaryHypothesis[]
  // ``roster``, ``notes``, ``legacy_notes``, ``notes_count_text``,
  // ``case_id``, ``generated_at`` flow through from the PDF builder but
  // the overview card doesn't render them today — keep them as opaque
  // pass-through so future additions don't require an API change.
  roster?: unknown
  notes?: unknown
  legacy_notes?: string
  notes_count_text?: string
  case_id?: string
  generated_at?: string
}

/** A hypothesis as the summary carries it (``_build_hypotheses_view``): the latest assessment. */
export interface CaseSummaryHypothesis {
  title: string
  claim: string
  status: 'open' | 'closed'
  verdict: HypothesisVerdict | null
  confidence: HypothesisConfidence | null
  /** Formatted by the server ("2026-03-03 09:00 UTC", "—" before the first assessment). */
  assessedAt: string
  author: string | null
  rationale: string
  assessmentCount: number
  history: { verdict: HypothesisVerdict; confidence: HypothesisConfidence; date: string }[]
  /** Titles of the investigations that are evidence for it. */
  evidence: string[]
}

export interface CaseSummaryInvestigation {
  title: string
  source: string | null
  author: string | null
  createdAt: string
  updatedAt: string
  noteCount: number
  noteCountText: string
  nodeCount: number
  edgeCount: number
  includedInReport: boolean
  // Snapshot fields from the research-promoted variants (optional / "" when
  // the investigation is a plain user one rather than promoted from a
  // hypothesis run).
  summary: string
  confidence: string
  confidenceText: string
  evidenceCount: number
  /** Numbered as the report cites them; `label` is the source's name, `raw` an external
   *  reference (an in-app id is never printed). Older payloads carry `text` + `source` only. */
  evidence: { text: string; source: string; label?: string; raw?: string; index?: number }[]
  /** A saved research report (PRD-0076); its narrative prints in the PDF, not on the card. */
  isReport?: boolean
  /** A saved finding's narrative (its description note), not counted among the notes. */
  description?: string
  // Compact per-investigation lists (entities, persons, documents, notes,
  // top relationship types). Render best-effort; missing/empty arrays are
  // fine for the card.
  persons: { displayName: string; identifier: string | null; label: string | null }[]
  personsTotal: number
  documents: { displayName: string; identifier: string | null; label: string | null }[]
  documentsTotal: number
  entities: { displayName: string; identifier: string | null; label: string | null }[]
  entitiesTotal: number
  notes: { author: string; createdAt: string; text: string }[]
  relations: { type: string; count: number }[]
}

export type CaseRole = 'owner' | 'collaborator' | 'viewer'
/** Grantable roles when sharing a case (owner is never granted directly). */
export type GrantableCaseRole = 'collaborator' | 'viewer'

export interface CaseMember {
  user_id: string
  username: string
  full_name: string | null
  email: string | null
  role: CaseRole
  invited_by: string | null
  joined_at: string
}

export const casesApi = {
  // List all cases
  list: () => apiClient.get<Case[]>('/v1/cases'),

  // Get case by ID
  get: (caseId: string) => apiClient.get<Case>(`/v1/cases/${caseId}`),

  // Create new case
  create: (data: CreateCaseRequest) => apiClient.post<Case>('/cases', data),

  // Update case
  update: (caseId: string, data: UpdateCaseRequest) =>
    apiClient.patch<Case>(`/cases/${caseId}`, data),

  // Delete case
  delete: (caseId: string) => apiClient.delete<void>(`/cases/${caseId}`),

  // Reinitialize case indexes
  reinitializeIndexes: (caseId: string) =>
    apiClient.post<void>(`/cases/${caseId}/reinitialize-indexes`),

  // Recompute and persist entity/relationship counts via a live Neo4j scan.
  refreshCounts: (caseId: string) =>
    apiClient.post<{ entity_count: number; relationship_count: number }>(
      `/v1/cases/${caseId}/refresh-counts`
    ),

  // ── Members / sharing ──────────────────────────────────────────────────
  listMembers: (caseId: string) => apiClient.get<CaseMember[]>(`/v1/cases/${caseId}/members`),
  addMember: (
    caseId: string,
    body: { username?: string; user_id?: string; role: GrantableCaseRole }
  ) => apiClient.post<CaseMember>(`/v1/cases/${caseId}/members`, body),
  updateMember: (caseId: string, userId: string, role: GrantableCaseRole) =>
    apiClient.patch<CaseMember>(`/v1/cases/${caseId}/members/${userId}`, { role }),
  removeMember: (caseId: string, userId: string) =>
    apiClient.delete<void>(`/v1/cases/${caseId}/members/${userId}`),

  // ── Case Summary ───────────────────────────────────────────────────────
  // Cheap pre-flight: returns whether a cached summary exists + how many
  // investigations are eligible. Used by the overview card to decide which
  // empty-state CTA to show without paying for the full payload fetch.
  getCaseSummaryStatus: (caseId: string) =>
    apiClient.get<CaseSummaryStatus>(`/v1/cases/${caseId}/summary/status`),
  // Full cached payload. Backend returns 404 when nothing has been
  // generated yet — callers should treat 404 as "show Generate CTA".
  getCaseSummary: (caseId: string) =>
    apiClient.get<CaseSummaryEnvelope>(`/v1/cases/${caseId}/summary`),
  // Trigger an LLM-backed rebuild + persist. Locale defaults to "en"
  // on the BE; pass "cs" (Czech) or "th" (Thai).
  generateCaseSummary: (caseId: string, locale: string = 'en') =>
    apiClient.post<CaseSummaryEnvelope>(`/v1/cases/${caseId}/summary/generate`, { locale }),

  // Documents
  getDocuments: (caseId: string, options?: { status?: string; limit?: number; offset?: number }) =>
    apiClient.get<Document[]>(`/cases/${caseId}/documents`, {
      params: options,
    }),

  /** v1 document list — membership-gated, no workflow status. The legacy
   * `/cases/{id}/documents` above needs Temporal and answers 503 today. */
  listDocuments: (caseId: string, options?: { limit?: number; offset?: number }) =>
    apiClient.get<Document[]>(`/v1/cases/${caseId}/documents`, { params: options }),

  // Graph
  getGraph: (caseId: string, options?: { limit?: number; minimal?: boolean }) =>
    apiClient.get<GraphResponse>(`/cases/${caseId}/graph`, { params: options }),

  // Filtered Graph (for Document Explorer)
  getFilteredGraph: (
    caseId: string,
    documentIds: string[],
    options?: {
      include_case_nodes?: boolean
      limit?: number
      minimal?: boolean
    }
  ) =>
    apiClient.get<GraphResponse>(`/cases/${caseId}/graph/filtered`, {
      params: {
        document_ids: documentIds.join(','),
        include_case_nodes: options?.include_case_nodes ?? false,
        limit: options?.limit,
        minimal: options?.minimal,
      },
    }),

  downloadDocument: (caseId: string, documentId: string) =>
    apiClient.get<Blob>(`/v1/cases/${caseId}/documents/${documentId}/download`, {
      responseType: 'blob',
    }),

  deleteDocument: (caseId: string, documentId: string) =>
    apiClient.delete<void>(`/cases/${caseId}/documents/${documentId}`),

  /** @deprecated Use bulkDeleteDocuments with body instead */
  bulkDeleteDocumentsLegacy: (caseId: string, documentIds: string[]) =>
    apiClient.delete<{
      deleted: number
      failed: number
      errors: Array<{ document_id: string; filename: string; error: string }>
    }>(`/cases/${caseId}/documents`, { document_ids: documentIds }),

  pinNodeToCase: (caseId: string, nodeId: string, pin: boolean = true) =>
    apiClient.post<{
      id: string
      labels: string[]
      properties: Record<string, unknown>
      pinned: boolean
    }>(`/cases/${caseId}/nodes/${nodeId}/pin`, { pin }),

  bulkPinNodesToCase: (caseId: string, nodeIds: string[], pin: boolean = true) =>
    apiClient.post<{
      updated: number
      failed: number
      updated_ids: string[]
      pin: boolean
    }>(`/cases/${caseId}/nodes/bulk-pin`, { node_ids: nodeIds, pin }),

  getChunks: (caseId: string, documentIds?: string[]) => {
    const params =
      documentIds && documentIds.length > 0 ? `?document_ids=${documentIds.join(',')}` : ''
    return apiClient.get<{
      chunks: Array<{
        id: string
        text: string
        chunk_index: number
        document_id: string
        document_name: string
        document_path: string
        metadata: Record<string, unknown>
      }>
      total: number
    }>(`/cases/${caseId}/chunks${params}`)
  },

  // Async Document Analysis
  startAnalysis: (caseId: string, documentId: string, force?: boolean) =>
    apiClient.post<StartAnalysisResponse>(
      `/cases/${caseId}/documents/${documentId}/analyze${force ? '?force=true' : ''}`
    ),

  getAnalysisStatus: (caseId: string, documentId: string) =>
    apiClient.get<AnalysisStatusResponse>(
      `/cases/${caseId}/documents/${documentId}/analysis-status`
    ),

  /** List all data-lake documents imported into a case (child docs with extraction scopes). */
  getLakeImports: (caseId: string) =>
    apiClient.get<LakeImport[]>(`/v1/cases/${caseId}/lake-imports`),

  /**
   * Delete a lake import from a case.
   * Removes the DocumentExtractionScope, S3 file copy, and child Document row.
   * The original lake document is not affected.
   */
  deleteLakeImport: (caseId: string, documentId: string) =>
    apiClient.delete<void>(`/v1/cases/${caseId}/lake-imports/${documentId}`),

  /**
   * Mark extraction schema as reviewed for a tabular document.
   * Sets reviewed_at timestamp and reviewed_by user ID on the extraction scope.
   */
  markSchemaReviewed: (caseId: string, documentId: string) =>
    apiClient.patch<{ message: string; reviewed_at: string }>(
      `/v1/cases/${caseId}/lake-imports/${documentId}/mark-reviewed`
    ),

  getCaseDocuments: (
    caseId: string,
    options?: {
      offset?: number
      limit?: number
      status?: string[]
      /** Only the documents whose file name holds this text. */
      search?: string
    }
  ) => {
    let url = `/v1/cases/${caseId}/documents`
    const searchParams = new URLSearchParams()
    if (options?.offset !== undefined) searchParams.set('offset', String(options.offset))
    if (options?.limit !== undefined) searchParams.set('limit', String(options.limit))
    options?.status?.forEach(s => searchParams.append('status', s))
    if (options?.search) searchParams.set('search', options.search)

    const qs = searchParams.toString()
    if (qs) url += `?${qs}`

    return apiClient.get<CaseDocumentResponse[]>(url)
  },

  getCaseFolderTree: (
    caseId: string,
    opts?: {
      path?: string
      limit?: number
      offset?: number
      status?: CaseDocumentStatusFilter
      sort?: CaseDocumentSort
    }
  ) => {
    const params = new URLSearchParams()
    if (opts?.path) params.set('path', opts.path)
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit))
    if (opts?.offset !== undefined) params.set('offset', String(opts.offset))
    if (opts?.status) params.set('status', opts.status)
    if (opts?.sort) {
      params.set('sort', opts.sort.by)
      params.set('order', opts.sort.order)
    }
    const qs = params.toString()
    return apiClient.get<CaseFolderTreeResponse>(
      `/v1/cases/${caseId}/folder-tree${qs ? `?${qs}` : ''}`
    )
  },

  // Pre-batch collision check: which target paths ("{relative_path}/{name}"
  // or "{name}") already exist in the case. Powers the "replace or cancel"
  // prompt before any document is uploaded.
  checkDocumentConflicts: (caseId: string, paths: string[]) =>
    apiClient.post<{ conflicts: string[] }>(`/v1/cases/${caseId}/documents/check-conflicts`, {
      paths,
    }),

  getPipelineActivities: () =>
    apiClient.get<PipelineActivityTemplate[]>('/cases/pipeline-activities'),

  getPhase2Workflows: () =>
    apiClient.get<{ id: string; name: string; color: string; template: string }[]>(
      '/cases/phase2-workflows'
    ),

  getExtractionSchemas: () => apiClient.get<ExtractionSchema[]>('/v1/schemas'),

  getCasePipelineActivities: (caseId: string) =>
    apiClient.get<PipelineActivity[]>(`/v1/cases/${caseId}/pipeline-activities`),

  extractDocuments: (caseId: string, body: ExtractRequest) =>
    apiClient.post<ExtractResponse>(`/v1/cases/${caseId}/extract`, body),

  /** The newest runs of the case, newest first: 100 of them unless `limit` says otherwise (500 at most). */
  getExtractionRuns: (caseId: string, opts?: { limit?: number }) =>
    apiClient.get<ExtractionRun[]>(`/v1/cases/${caseId}/extraction-runs`, { params: opts }),

  /**
   * The status and stage of the named documents, at most 500 of them: the read a page repeats
   * for the rows that are on their way. A document the case no longer has is left out.
   */
  getDocumentProgress: (caseId: string, documentIds: readonly string[]) =>
    apiClient.post<CaseDocumentProgress[]>(`/v1/cases/${caseId}/documents/progress`, {
      document_ids: documentIds,
    }),

  getExtractionRunDetail: (caseId: string, runId: string) =>
    apiClient.get<ExtractionRunDetail>(`/v1/cases/${caseId}/extraction-runs/${runId}`),

  getCaseDocumentDetail: (caseId: string, documentId: string) =>
    apiClient.get<CaseDocumentDetail>(`/v1/cases/${caseId}/documents/${documentId}`),

  /** Changes when the case received a document (ADR-0094): a day `YYYY-MM-DD`; collaborators only. */
  updateCaseDocument: (caseId: string, documentId: string, body: { received_at: string }) =>
    apiClient.patch<CaseDocumentUpdateResponse>(
      `/v1/cases/${caseId}/documents/${documentId}`,
      body
    ),

  updateTableProcessing: (caseId: string, documentId: string, mode: TabularExtractionMode) =>
    apiClient.patch<TableProcessingResponse>(
      `/v1/cases/${caseId}/documents/${documentId}/table-processing`,
      { mode }
    ),

  getCaseGraphEntity: (caseId: string, entityId: string) =>
    apiClient.get<CaseGraphEntityDetail>(
      `/v1/cases/${caseId}/graph/entities/${encodeURIComponent(entityId)}`
    ),

  getCaseGraphRelationship: (caseId: string, relationshipId: string) =>
    apiClient.get<CaseGraphRelationshipDetail>(
      `/v1/cases/${caseId}/graph/relationships/${encodeURIComponent(relationshipId)}`
    ),

  getCaseDocumentInternal: (caseId: string, documentId: string) =>
    apiClient.get<CaseDocumentInternal>(`/v1/cases/${caseId}/documents/${documentId}/internal`),

  resetExtraction: (caseId: string, documentIds: string[]) =>
    apiClient.post<{ reset: number; failed: number }>(`/v1/cases/${caseId}/reset-extraction`, {
      document_ids: documentIds,
    }),

  /** Queue a recording's analysis on the video lane (PRD-0074); 202 with the job, 409 while
   *  Advanced Face Detection is off or the document is in flight, 422 for a file that is not a video. */
  startVideoAnalysis: (caseId: string, documentId: string) =>
    apiClient.post<VideoAnalysisStart>(
      `/v1/cases/${caseId}/documents/${documentId}/video-analysis`
    ),

  deleteCaseDocument: (caseId: string, documentId: string) =>
    apiClient.delete<void>(`/v1/cases/${caseId}/documents/${documentId}`),

  // Case settings
  getCaseSettings: (caseId: string) =>
    apiClient.get<CaseSettingsResponse>(`/v1/cases/${caseId}/settings`),

  updateCaseSettings: (caseId: string, data: CaseSettingsUpdate) =>
    apiClient.patch<CaseSettingsResponse>(`/v1/cases/${caseId}/settings`, data),

  resetCase: (caseId: string) =>
    apiClient.post<{
      documents_reset: number
      neo4j_nodes_deleted: number
      workflows_deleted: number
      extraction_runs_deleted: number
      notes_deleted: number
      canvases_deleted: number
      saved_queries_deleted: number
      agent_searches_deleted: number
      extraction_scopes_deleted: number
      geofences_deleted: number
    }>(`/v1/cases/${caseId}/reset`),

  bulkDeleteDocuments: (
    caseId: string,
    body: { document_ids?: string[]; folder_paths?: string[] }
  ) =>
    apiClient.post<{ deleted: number; failed: number }>(
      `/v1/cases/${caseId}/documents/bulk-delete`,
      body
    ),
}
